import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AuthLayout } from '../components';
import Homescreen_icon from '../../home/assets/Homescreen.png';
import { Button } from '@/components';
import {
  getSubscriptionPlans,
  createSubscriptionOrder,
  verifySubscriptionPayment,
} from '../api';
import { handleApiError } from '@/common/utils';
import {
  AUTH_BUTTON_CLASS,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '../lib/authStyles';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/** Marketing features shown in Figma "What's Included" */
const DEFAULT_INCLUDED_FEATURES = [
  'Unlimited Bulk WhatsApp Messaging',
  'Individual Broadcast messaging',
  'Automatic Lead Capture',
  'AI-Powered Chatbot (24×7)',
  'Fully Automated CRM System',
  'Intelligent Lead Assignment Engine',
  'Automated Follow-ups & Reminders',
  'Digital Business Card',
];

function getDisplayFeatures() {
  return DEFAULT_INCLUDED_FEATURES;
}

export function WhatsAppActivatedPage() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('company_id');

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState('');

  const {
    data: plansData,
    isLoading: isPlansLoading,
    isError,
    error,
    refetch: refetchPlans,
  } = useQuery({
    queryKey: ['subscription-plans', 'whatsapp', appliedCoupon],
    queryFn: () => getSubscriptionPlans('whatsapp', appliedCoupon),
    retry: false,
  });

  const [plans, setPlans] = useState(null);
  const [lastSuccessfulCoupon, setLastSuccessfulCoupon] = useState('');

  useEffect(() => {
    if (plansData) {
      setPlans(plansData);
      if (appliedCoupon) {
        if (appliedCoupon !== lastSuccessfulCoupon) {
          toast.success('Coupon applied successfully!');
          setLastSuccessfulCoupon(appliedCoupon);
        }
      } else if (lastSuccessfulCoupon) {
        setLastSuccessfulCoupon('');
      }
    }
  }, [plansData, appliedCoupon, lastSuccessfulCoupon]);

  useEffect(() => {
    if (isError && appliedCoupon) {
      const errorMsg = error?.response?.data?.detail || 'Invalid coupon code.';
      toast.error(errorMsg);
      setAppliedCoupon('');
      setCouponCode('');
    }
  }, [isError, appliedCoupon, error]);

  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (plans && !selectedPlanId) {
      const defaultPlan =
        plans.find((p) => p.slug === 'whatsapp-pro') || plans[0];
      if (defaultPlan) {
        setSelectedPlanId(defaultPlan.id);
      }
    }
  }, [plans, selectedPlanId]);

  const activePlan = plans?.find((p) => p.id === selectedPlanId) || plans?.[0];

  const planName = activePlan?.name || '';
  const planPrice = activePlan ? parseFloat(activePlan.price) : 0;
  const planSlug = activePlan?.slug || '';

  const gstAmount = planPrice * 0.18;
  const totalDue = planPrice + gstAmount;

  const totalDueFormatted = totalDue.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const { mutate: createOrder, isPending: isPaying } = useMutation({
    mutationFn: createSubscriptionOrder,
    onSuccess: async (orderData) => {
      const { razorpay_key_id, razorpay_subscription_id } = orderData;

      if (razorpay_key_id) {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error(
            'Failed to load Razorpay SDK. Please check your internet connection.',
          );
          return;
        }

        const options = {
          key: razorpay_key_id,
          subscription_id: razorpay_subscription_id,
          name: 'Aexon',
          description: `${planName} Subscription`,
          handler: async function (response) {
            const toastId = toast.loading('Payment authorized. Verifying...', {
              id: 'payment-verification',
            });
            try {
              await verifySubscriptionPayment({
                companyId,
                subscriptionId:
                  response.razorpay_subscription_id || razorpay_subscription_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });
              toast.success('Payment verified & subscription activated!', {
                id: toastId,
              });
              navigate('/dashboard', { replace: true });
            } catch (err) {
              console.error('Payment verification error:', err);
              toast.error(
                err?.response?.data?.detail ||
                  err?.message ||
                  'Payment verification failed. Please contact support.',
                { id: toastId },
              );
            }
          },
          theme: {
            color: '#50AB3D',
          },
          modal: {
            ondismiss: function () {
              toast.error('Payment cancelled by user.');
            },
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.loading('Simulating payment...', { id: 'dev-payment-sim' });
        setTimeout(async () => {
          toast.dismiss('dev-payment-sim');
          const toastId = toast.loading(
            'Simulated payment authorized. Verifying...',
            { id: 'dev-payment-sim-verifying' },
          );
          try {
            await verifySubscriptionPayment({
              companyId,
              subscriptionId: razorpay_subscription_id,
              paymentId: `pay_dev_${Math.random().toString(36).substr(2, 9)}`,
              signature: `sig_dev_${Math.random().toString(36).substr(2, 9)}`,
            });
            toast.success('Payment verified & subscription activated!', {
              id: toastId,
            });
            navigate('/dashboard', { replace: true });
          } catch (err) {
            console.error('Payment verification error:', err);
            toast.error(
              err?.response?.data?.detail ||
                err?.message ||
                'Payment verification failed. Please contact support.',
              { id: toastId },
            );
          }
        }, 2000);
      }
    },
    onError: (err) => {
      handleApiError(err, 'Failed to initiate payment. Please try again.');
    },
  });

  const handleBack = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const handlePay = () => {
    if (!companyId) {
      toast.error('Company details not found. Please register first.');
      navigate('/register');
      return;
    }
    createOrder({ companyId, planSlug, coupon: appliedCoupon });
  };

  const features = getDisplayFeatures();

  return (
    <AuthLayout rightImage={Homescreen_icon} rightAlt="Aexon Assistant Mockup">
      <div className="animate-fadeIn">
        {/* Success icon + halo (Figma) */}
        <div className="relative w-[72px] h-[72px] flex items-center justify-center mb-4">
          <div className="absolute inset-0 rounded-full bg-primary/15" />
          <div className="relative w-12 h-12 rounded-full bg-primary flex items-center justify-center">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12.75l6 6 9-13.5"
              />
            </svg>
          </div>
        </div>

        <div className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-primary/10 rounded-full w-fit mb-4">
          <span className="w-2.5 h-2.5 rounded-full bg-primary" />
          <span className="text-h6-medium font-sf-pro-display text-primary">
            Account Activated
          </span>
        </div>

        <h1 className={AUTH_TITLE_CLASS}>Your Account Is Activated!</h1>
        <h2 className={AUTH_SUBTITLE_CLASS}>
          Your WhatsApp integration is now ready. Review your subscription plan
          and continue to payment to unlock all premium features.
        </h2>

        <div className="w-full max-w-[710px] flex flex-col gap-6">
          {isPlansLoading && !plans ? (
            <div className="h-[400px] w-full flex items-center justify-center">
              <svg
                className="animate-spin h-10 w-10 text-primary"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : isError && !plans ? (
            <div className="h-[300px] w-full flex flex-col items-center justify-center gap-4 text-center px-6">
              <p className="text-red-500 font-sf-pro-display text-lg">
                Failed to load subscription plans.
              </p>
              <Button
                size="lg"
                type="button"
                variant="primary"
                className={`${AUTH_BUTTON_CLASS} w-full max-w-[160px] h-[40px]`}
                onClick={() => refetchPlans()}
              >
                Retry
              </Button>
            </div>
          ) : !plans || plans.length === 0 ? (
            <div className="h-[300px] w-full flex flex-col items-center justify-center text-center px-6">
              <p className="text-text-secondary font-sf-pro-display text-lg">
                No subscription plans available at the moment.
              </p>
            </div>
          ) : (
            <>
              {/* Plans List */}
              <div className="flex flex-col gap-4">
                {plans.map((plan, idx) => {
                  const isSelected = selectedPlanId === plan.id;

                  const iconsData = [
                    {
                      icon: (
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      ),
                      bgColor: 'bg-[#FEF7E6]',
                      textColor: 'text-[#F5A623]',
                    },
                    {
                      icon: (
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                      ),
                      bgColor: 'bg-[#E9F5EB]',
                      textColor: 'text-[#50AB3D]',
                    },
                    {
                      icon: (
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                          />
                        </svg>
                      ),
                      bgColor: 'bg-[#EBF3FF]',
                      textColor: 'text-[#2D7CFF]',
                    },
                  ];
                  const itemData = iconsData[idx % iconsData.length];

                  return (
                    <div
                      key={plan.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedPlanId(plan.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedPlanId(plan.id);
                        }
                      }}
                      className={`p-5 rounded-[16px] border cursor-pointer transition-all flex items-center gap-4 ${
                        isSelected
                          ? 'border-primary bg-white shadow-[0_4px_20px_rgba(80,171,61,0.08)]'
                          : 'border-border-default bg-white/50 hover:bg-white'
                      }`}
                    >
                      <div
                        className={`w-[52px] h-[52px] rounded-xl flex items-center justify-center shrink-0 ${itemData.bgColor} ${itemData.textColor}`}
                      >
                        {itemData.icon}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary capitalize font-medium text-lg font-sf-pro-display truncate">
                          {plan.name}
                        </p>
                        <p className="text-text-secondary text-sm font-sf-pro-display truncate mt-0.5">
                          {plan.description}
                        </p>
                      </div>

                      <div className="text-right shrink-0">
                        <p className="text-text-primary text-h3 font-sf-pro-display tabular-nums">
                          ₹{parseFloat(plan.price).toLocaleString('en-IN')}
                        </p>
                        <p className="text-text-secondary capitalize text-sm font-sf-pro-display mt-0.5">
                          {plan.billing_period}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Grey Box for Features, Coupon, Pay */}
              <div className="bg-[#F8F9F8] rounded-[16px] p-6 flex flex-col gap-6">
                <div>
                  <h3 className="font-sf-pro-display text-text-primary text-body-lg-medium text-left mb-4">
                    What&apos;s Included
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-left">
                    {features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2.5">
                        <span className="w-[18px] h-[18px] rounded-full bg-[#34C759] flex items-center justify-center shrink-0 mt-0.5">
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4.5 12.75l6 6 9-13.5"
                            />
                          </svg>
                        </span>
                        <span className="text-text-primary text-lg font-sf-pro-display !leading-snug">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      className="w-full bg-[#EBEFEA] border-none rounded-[8px] h-[44px] px-4 text-sm font-sf-pro-display focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setAppliedCoupon(couponCode)}
                    disabled={isPlansLoading}
                    className="h-[44px] px-6 bg-[#50AB3D] hover:bg-[#439232] disabled:opacity-75 text-white rounded-[8px] text-sm font-semibold font-sf-pro-display transition-colors whitespace-nowrap flex items-center justify-center gap-1.5"
                  >
                    Apply <span aria-hidden="true">&rarr;</span>
                  </button>
                </div>

                <div className="flex flex-col items-center gap-4 mt-2">
                  <Button
                    size="lg"
                    type="button"
                    variant="primary"
                    className={`${AUTH_BUTTON_CLASS} w-full sm:w-[320px] h-[48px]`}
                    onClick={handlePay}
                    disabled={isPaying || !activePlan}
                  >
                    {isPaying ? (
                      <span className="flex items-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5 text-surface-mint"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      <>
                        <span>Pay ₹{totalDueFormatted} via Razorpay</span>
                        <span>&rarr;</span>
                      </>
                    )}
                  </Button>

                  <div className="flex flex-col items-center gap-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="text-body-lg font-normal font-sf-pro-display text-text-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <svg
                        className="w-4 h-4 inline"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 19.5L8.25 12l7.5-7.5"
                        />
                      </svg>
                      <span>Back to Login</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
